import { useMemo } from "react";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { tarefaService } from "@/services/api/tarefa.service";
import { TarefaDTO, EditarTarefaGenericaPayload } from "@/types/tarefa.type";
import { TarefaStatus } from "@workflow/types";

export type TarefaAction = "concluir" | "cancelar" | "reabrir" | "emExecucao";

const ACTION_MESSAGES: Record<TarefaAction, string> = {
  concluir: "concluída",
  cancelar: "cancelada",
  reabrir: "reaberta",
  emExecucao: "colocada em execução",
};

export function useTarefaDetail(tarefaId: number) {
  const queryClient = useQueryClient();

  /**
   * Reconcilia o cache do detalhe com a resposta de uma mutation. Os endpoints de
   * mudança de status/edição retornam a Tarefa crua, SEM o descritor `conclusao`
   * (só o GET `/:id` o deriva). Sobrescrever direto apagaria o `conclusao` e faria
   * o painel degradar para o "Concluir" simples — inválido p/ tarefas com formulário
   * (ex.: estudo de verba). Como o descritor não depende do status, preservamos o do
   * cache atual quando a resposta não o traz.
   */
  const aplicarTarefaAtualizada = (updated: TarefaDTO) => {
    queryClient.setQueryData<TarefaDTO>(["tarefa", tarefaId], (atual) => ({
      ...updated,
      conclusao: updated.conclusao ?? atual?.conclusao,
    }));
  };

  const {
    data: tarefa,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["tarefa", tarefaId],
    queryFn: () => tarefaService.getTarefaById(tarefaId),
    enabled: !!tarefaId,
    staleTime: 1000 * 60 * 5,

    refetchOnMount: "always",

    retry: 1,
  });

  const actionMutation = useMutation({
    mutationFn: async (action: TarefaAction) => {
      // Usando switch para clareza e performance
      switch (action) {
        case "concluir":
          return await tarefaService.concluir(tarefaId);
        case "cancelar":
          return await tarefaService.cancelar(tarefaId);
        case "reabrir":
          return await tarefaService.reabrir(tarefaId);
        case "emExecucao":
          return await tarefaService.emExecucao(tarefaId);
        default:
          throw new Error("Ação inválida");
      }
    },
    onSuccess: (updatedTarefa, action) => {
      aplicarTarefaAtualizada(updatedTarefa);
      queryClient.invalidateQueries({ queryKey: ["tarefas-notificacoes"] });
      queryClient.invalidateQueries({ queryKey: ["tarefas-pendentes-count"] });
      queryClient.invalidateQueries({ queryKey: ["tarefa-mensagens", tarefaId] });

      toast.success(`Tarefa ${ACTION_MESSAGES[action]} com sucesso!`);
    },
    onError: (error) => {
      console.error(error.message || "Não foi possível processar a ação solicitada.");
    },
  });

  const updateDataPrazoMutation = useMutation({
    mutationFn: async (dataPrazo: Date | null) => {
      return await tarefaService.alterarDataPrazo(tarefaId, dataPrazo);
    },
    onSuccess: (updatedTarefa) => {
      aplicarTarefaAtualizada(updatedTarefa);
      queryClient.invalidateQueries({ queryKey: ["tarefas-notificacoes"] });
      queryClient.invalidateQueries({ queryKey: ["tarefas-pendentes-count"] });
      queryClient.invalidateQueries({ queryKey: ["tarefa-mensagens", tarefaId] });

      toast.success("Data prazo alterada com sucesso!");
    },
    onError: (error) => {
      console.error(error.message || "Não foi possível alterar o prazo da tarefa.");
    },
  });

  const editarGenericaMutation = useMutation({
    mutationFn: async (payload: EditarTarefaGenericaPayload) => {
      return await tarefaService.editarGenerica(tarefaId, payload);
    },
    onSuccess: (updatedTarefa) => {
      aplicarTarefaAtualizada(updatedTarefa);
      queryClient.invalidateQueries({ queryKey: ["tarefas-notificacoes"] });
      queryClient.invalidateQueries({ queryKey: ["tarefas-publicas"] });
      queryClient.invalidateQueries({ queryKey: ["tarefas-job"] });

      toast.success("Tarefa atualizada com sucesso!");
    },
    onError: (error) => {
      console.error(error.message || "Não foi possível salvar as alterações da tarefa.");
    },
  });

  const statusMenu = useMemo(() => {
    if (!tarefa) return null;

    return {
      isPendente: tarefa.status === TarefaStatus.PENDENTE,
      isEmExecucao: tarefa.status === TarefaStatus.EM_EXECUCAO,
      isConcluido: tarefa.status === TarefaStatus.CONCLUIDO,
      isCancelado: tarefa.status === TarefaStatus.CANCELADO,
      isArquivado: tarefa.status === TarefaStatus.ARQUIVADO,
      isFechada: [TarefaStatus.CONCLUIDO, TarefaStatus.CANCELADO, TarefaStatus.ARQUIVADO].includes(
        tarefa.status,
      ),
    };
  }, [tarefa?.status]);

  return {
    tarefa,
    isLoading,
    isError,
    isActionLoading: actionMutation.isPending,
    isUpdateDataPrazoLoading: updateDataPrazoMutation.isPending,
    statusMenu,
    isEditarGenericaLoading: editarGenericaMutation.isPending,
    handleAction: (action: TarefaAction) => actionMutation.mutate(action),
    handleUpdateDataPrazo: (dataPrazo: Date | null) => updateDataPrazoMutation.mutate(dataPrazo),
    handleEditarGenerica: (payload: EditarTarefaGenericaPayload) =>
      editarGenericaMutation.mutateAsync(payload),
    updateLocalCache: (newData: TarefaDTO) =>
      queryClient.setQueryData(["tarefa", tarefaId], newData),
  };
}
